import {arc} from 'd3-shape';
import {path} from 'd3-path';

const d3 = {arc, path};

const arcViewParameters = (cordWidth, radius) => {
    const radian = 2 * (Math.asin(cordWidth / (radius * 2)));
    const arc = radian * radius;

    return {
        "radian": radian,
        "arc": arc
    }
}

const getBasePairFromPoint = (event, circle, $boxContainer, sequenceLength) => {
    let tmpCursorBp = radianFromPoint(event.x - circle.center.x - $boxContainer.x, event.y - circle.center.y - $boxContainer.y)
    if (tmpCursorBp < 0) {
        tmpCursorBp += (2 * Math.PI);
    }
    return basePairFromRadian(tmpCursorBp, sequenceLength);
}

const spanPath = (radius, height, startRadian, endRadian) => {
    let path = d3.arc();
    return path({
        outerRadius: radius,
        innerRadius: radius - height,
        startAngle: startRadian,
        endAngle: endRadian
    });
}

const radianFromPoint = (x, y) => {
    return -(Math.atan2(x, y) + Math.PI);
}

const radianFromBasePair = (basePair, sequenceLength) => {
    return (2 / sequenceLength) * Math.PI * basePair;
}

const basePairFromRadian = (radian, sequenceLength) => {
    return radian * (sequenceLength / 2) * (1 / Math.PI);
}

const degreeFromBasePair = (basePair, sequenceLength) => {
    return radianFromBasePair(basePair, sequenceLength) * (180 / Math.PI)
}

const pointFromBasePair = (basePair, radius, sequenceLength) => {
    return {
        "x": radius * Math.cos(radianFromBasePair(basePair, sequenceLength) - (Math.PI / 2)),
        "y": radius * Math.sin(radianFromBasePair(basePair, sequenceLength) - (Math.PI / 2))
    };
}

const pointFromRadian = (radius, radianPosition) => {
    return [radius * Math.sin(radianPosition), radius * Math.cos(radianPosition)]
}

const linePath = (position, y1, y2, sequenceLength) => {
    const path = new CircularShape(y1, sequenceLength);
    path.moveTo(position)
    path.lineTo(position, y1 - y2)
    return path.toString();
}

const cutPath = (radius, middle, height, cut5, cut3, sequenceLength) => {
    const path = new CircularShape(radius, sequenceLength);
    path.moveTo(cut5)
    path.lineTo(cut5, radius - middle, true)
    path.arcTo(cut3, cut5 > cut3)
    path.lineTo(cut3, radius - height)
    return path.toString();
}

const tail1Path = (radius, height, start, middle, end, sequenceLength) => {
    const path = new CircularShape(radius, sequenceLength);
    path.moveTo(start)
    path.lineTo(middle, radius - height, true)
    return path.toString();
}

const tail2Path = (radius, height, start, middle, end, sequenceLength) => {
    const path = new CircularShape(radius, sequenceLength);
    path.moveTo(middle)
    path.lineTo(end, radius + height)
    return path.toString();
}

const annotationPath = (radius, height, start, end, sequenceLength, direction = 0, arrowWidth = .4) => {
    if (direction === 0) {
        return spanPath(radius, height, radianFromBasePair(start, sequenceLength), radianFromBasePair(end, sequenceLength))
    }
    const maxBasePairsToRender = 250;
    const maxRadiusForRendering = 500;
    const path = new CircularShape(radius, sequenceLength);
    const anticlockwise = false;
    path.moveTo(start);
    if (radius > maxRadiusForRendering && (end - start) > maxBasePairsToRender) {
        path.stretchClockwiseArcTo(start, end, maxBasePairsToRender)
    } else {
        path.arcTo(end, anticlockwise, false, false, ((end - start) > (sequenceLength / 2)));
    }

    direction === 1 ? path.arrowRight(end, arrowWidth, -height) : path.lineTo(end, radius - height, true);

    if (radius > maxRadiusForRendering && (end - start) > maxBasePairsToRender) {
        path.stretchAntiClockwiseArcTo(start, end, maxBasePairsToRender)
    } else {
        path.arcTo(start, !anticlockwise, false, false, (end - start > sequenceLength / 2));
    }
    direction === 2 || direction === -1 ? path.arrowLeft(start, arrowWidth, -height, true) : path.closePath();
    return path.toString();
}


const codonPath = (radius, height, start, end, direction, arrowWidth, sequenceLength) => {
    const path = new CircularShape(radius, sequenceLength);

    path.moveTo(start)
    path.arcTo(end)
    if (direction === 1) {
        path.arrowRight(end, arrowWidth, -height)
    } else {
        path.arrowRight(end, -arrowWidth, -height)
    }
    path.arcTo(start, true)
    if (direction === 1) {
        path.arrowLeft(start, -arrowWidth, -height)
    } else {
        path.arrowLeft(start, arrowWidth, -height)
    }
    path.closePath()
    return path.toString();
}

const endCodingSequencePath = (radius, height, position, direction, arrowWidth, sequenceLength) => {
    const path = new CircularShape(radius, sequenceLength);
    path.moveTo(position)
    if (direction === 1) {
        path.arrowRight(position, arrowWidth, -height)
        path.arrowLeft(position, -arrowWidth, -height)
    } else {
        path.arrowRight(position, -arrowWidth, -height)
        path.arrowLeft(position, arrowWidth, -height)
    }
    return path.toString();
}

const edgeBrushPath = (radius, height, radianPosition, enableMarker) => {
    let path
    if (enableMarker) {
        path = spanPath(radius, height, radianPosition, radianPosition);
        const radiusMarkerHeight = 10;
        const outerPoint = pointFromRadian(radius, radianPosition)
        const innerPoint = pointFromRadian(radius - height, radianPosition)
        const markerHalfCoordinate1 = pointFromRadian(radius + radiusMarkerHeight, radianPosition)
        const markerHalfCoordinate2 = pointFromRadian(radius, radianPosition)
        path = `
            M${innerPoint[0]},${innerPoint[1] * -1}
            L${outerPoint[0]},${outerPoint[1] * -1}
            A${radiusMarkerHeight / 2},${radiusMarkerHeight / 2},0,0,0,${markerHalfCoordinate1[0]},${(markerHalfCoordinate1[1]) * -1}
            A${radiusMarkerHeight / 2},${radiusMarkerHeight / 2},0,0,0,${markerHalfCoordinate2[0]},${(markerHalfCoordinate2[1]) * -1}
            `;
    } else {
        path = spanPath(radius, height, radianPosition, radianPosition);
    }
    return path;
}

const CircularShape = class {
    constructor(radius, sequenceLength) {
        this.radius = radius;
        this.sequenceLength = sequenceLength
    }

    moveTo(basePair) {
        const point = this.point(basePair);
        this.path = `M
            ${point.x},
            ${point.y}
        `;
    }

    lineTo(position, radius = null, newRadius = false, close = false) {
        const point = this.point(position, radius);
        this.path += `L
            ${point.x},
            ${point.y}
        `;
        this.radius = newRadius ? radius : this.radius
        if (close) {
            this.closePath()
        }
    }

    arcTo(position, anticlockwise = false, radius = null, close = false, overHalf = false) {
        const point = this.point(position, radius);
        const clockwise = anticlockwise ? 0 : 1;
        this.path += `A
            ${this.radius},${this.radius},
            0,
            ${overHalf ? 1 : 0},
            ${clockwise},
            ${point.x},
            ${point.y}
        `;
        if (close) {
            this.closePath()
        }
    }

    stretchClockwiseArcTo(init, term, minStretch) {
        let tempTerm;
        for (tempTerm = init; tempTerm <= term; tempTerm += minStretch) {
            if ((tempTerm + minStretch) >= term) {
                tempTerm = term;
            }
            this.arcTo(tempTerm, false);
        }
    }

    stretchAntiClockwiseArcTo(init, term, minStretch) {
        let tempInit;
        for (tempInit = term; tempInit >= init; tempInit -= minStretch) {
            if ((tempInit - minStretch) <= init) {
                tempInit = init;
            }
            this.arcTo(tempInit, true);
        }
    }

    arrowRight(position, arrowLength, height = 0, close = false) {
        this.lineTo(position + arrowLength, this.radius + (height / 2))
        this.lineTo(position, this.radius + height)
        this.radius += height
        if (close) {
            this.closePath()
        }
    }

    arrowLeft(position, arrowLength, height = 0, close = false) {
        this.radius -= (height / 2);
        this.lineTo(position - arrowLength)
        if (close) {
            this.closePath()
        }
    }

    point(basePair, radius = null) {
        const radiusTmp = radius ? radius : this.radius;
        return pointFromBasePair(basePair, radiusTmp, this.sequenceLength);
    }

    closePath() {
        this.path += "Z";
    }

    toString() {
        return this.path.replace(/(\r\n|\n|\s|\r)/gm, "");
    }
}

export {
    arcViewParameters,
    getBasePairFromPoint,
    radianFromPoint,
    radianFromBasePair,
    degreeFromBasePair,
    basePairFromRadian,
    pointFromBasePair,
    spanPath,
    linePath,
    cutPath,
    tail1Path,
    tail2Path,
    annotationPath,
    codonPath,
    endCodingSequencePath,
    edgeBrushPath
}